import { InputHTMLAttributes, forwardRef, useCallback, useEffect } from "react";
import Combobox from "./combobox";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import { setSiteList } from "store/appSlice";
import { siteList } from "api/site";

interface SitesComboboxProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  extra?: string;
  extraContainer?: string;
  variant?: "solid" | "outline";
  state?: "error" | "success"; // Specific states for styling
  readonly?: boolean;
  sizes?: "lg" | "md" | "sm" | "xs";
  onChange?: (val: any) => void;
  color?: "primary" | "secondary";
  rounded?: string;
  loading?: boolean;
  filter?: boolean;
}

const SitesCombobox = forwardRef<HTMLInputElement, SitesComboboxProps>(
  (
    {
      id,
      value,
      type = "text",
      placeholder,
      sizes = "lg",
      variant = "outline",
      color = "primary",
      rounded = "rounded-2xl",
      state,
      disabled,
      readonly,
      loading = false,
      extra = "",
      extraContainer = "",
      onChange,
      onBlur,
      onFocus,
      filter = false,
      ...rest
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const sites = useSelector(accountSiteList, shallowEqual);

    const fetchSitelist = useCallback(async () => {
      dispatch(setSiteList([]));
      const { data, error, isLoading, status } = await siteList();

      if (status === 200) {
        const sortedData = data.sort(
          (a: { name: string }, b: { name: any }) => {
            return a.name.localeCompare(b.name);
          }
        );
        dispatch(setSiteList(sortedData));
        localStorage.setItem("siteList", JSON.stringify(sortedData));
      }
    }, []);

    useEffect(() => {
      if (sites.length === 0 || localStorage.getItem("siteList") === null) {
        fetchSitelist();
      }
    }, [sites]);

    return (
      <Combobox
        id="select-site-combobox"
        placeholder="Select site"
        variant={variant}
        color={color}
        sizes={sizes}
        value={value}
        onChange={onChange}
        items={sites}
        labelKey="name"
        valueKey="id"
        rounded={rounded}
        disabled={loading}
        filter={filter}
      />
    );
  }
);

export default SitesCombobox;
